import { useEffect, useState } from "react";
import { FileText, X, Trash2, Check, ExternalLink, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Les navigateurs n'affichent pas le HEIC/HEIF : on montre une icône à la place.
const isDisplayableImage = (ct) => !!ct && ct.startsWith("image/") && !/hei[cf]/i.test(ct);
const isPdf = (ct, name) => ct === "application/pdf" || /\.pdf$/i.test(name || "");

export const publicDocUrl = (rid, docId) => `${BACKEND}/api/public/booking/reservation/${rid}/documents/${docId}`;

// Vignette carrée : image réelle si possible, sinon tuile « PDF » / « Fichier ».
function Tile({ src, contentType, filename, className = "" }) {
  const [broken, setBroken] = useState(false);
  if (src && isDisplayableImage(contentType) && !broken) {
    return (
      <img src={src} alt={filename} loading="lazy" onError={() => setBroken(true)}
        className={`h-full w-full object-cover ${className}`} />
    );
  }
  return (
    <div className={`flex h-full w-full flex-col items-center justify-center gap-1 bg-neutral-100 pb-5 text-neutral-500 ${className}`}>
      <FileText className="h-6 w-6" />
      <span className="text-[10px] font-bold uppercase">{isPdf(contentType, filename) ? "PDF" : "Fichier"}</span>
    </div>
  );
}

// Miniature d'un document déjà déposé (servi par le backend). Clic -> visionneuse.
export function DocThumb({ rid, doc, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(doc)} data-testid={`doc-thumb-${doc.id}`}
      className="group relative aspect-square w-full overflow-hidden rounded-lg border border-neutral-200 bg-white text-left focus:outline-none focus:ring-2 focus:ring-brand"
      title={`Voir ${doc.filename}`}>
      <Tile src={publicDocUrl(rid, doc.id)} contentType={doc.content_type} filename={doc.filename} />
      <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-1 text-[10px] font-semibold text-white">{doc.filename}</span>
    </button>
  );
}

// Miniature d'un fichier local (pas encore envoyé) — formulaire de réservation.
export function LocalFileThumb({ file, onRemove, testId }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!isDisplayableImage(file.type)) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-neutral-200 bg-white" data-testid={testId}>
      <Tile src={url} contentType={file.type} filename={file.name} />
      <span className="absolute inset-x-0 bottom-0 truncate bg-black/55 px-1.5 py-1 text-[10px] font-semibold text-white">{file.name}</span>
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label={`Retirer ${file.name}`} data-testid={testId ? `${testId}-remove` : undefined}
          className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-white/95 text-neutral-700 shadow hover:bg-red-50 hover:text-red-600">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// Visionneuse plein écran : l'élève vérifie son document puis le garde ou le supprime.
export function DocViewer({ rid, doc, onClose, onDelete, deleting }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
    if (!doc) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doc, onClose]);
  if (!doc) return null;
  const url = publicDocUrl(rid, doc.id);
  const img = isDisplayableImage(doc.content_type) && !broken;
  const pdf = isPdf(doc.content_type, doc.filename);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-6" onClick={onClose} data-testid="doc-viewer">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 border-b border-neutral-200 px-4 py-3">
          <p className="min-w-0 truncate text-sm font-bold text-neutral-900" data-testid="doc-viewer-name">{doc.filename}</p>
          <button type="button" onClick={onClose} aria-label="Fermer" data-testid="doc-viewer-close" className="shrink-0 rounded-lg p-1.5 text-neutral-500 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-neutral-100">
          {img ? (
            <img src={url} alt={doc.filename} onError={() => setBroken(true)} className="mx-auto max-h-[65vh] w-auto object-contain" data-testid="doc-viewer-image" />
          ) : pdf ? (
            <iframe title={doc.filename} src={url} className="h-[65vh] w-full border-0 bg-white" data-testid="doc-viewer-pdf" />
          ) : (
            <div className="flex flex-col items-center justify-center gap-2 p-10 text-neutral-500">
              <FileText className="h-10 w-10" />
              <p className="text-sm">{"Aperçu indisponible pour ce format."}</p>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-neutral-200 px-4 py-3">
          <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand hover:underline" data-testid="doc-viewer-open">
            <ExternalLink className="h-3.5 w-3.5" /> Ouvrir dans un onglet
          </a>
          <div className="flex gap-2">
            {onDelete && (
              <button type="button" onClick={() => onDelete(doc)} disabled={deleting} data-testid="doc-viewer-delete"
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-100 disabled:opacity-50">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Supprimer
              </button>
            )}
            <button type="button" onClick={onClose} data-testid="doc-viewer-keep"
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">
              <Check className="h-4 w-4" /> Garder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
